import classNames from 'classnames';
import { useId, type CSSProperties, type ReactNode } from 'react';

import { css, keyframes } from '@sorare/css';

import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { unitMapping, type Unit } from '#src/lib/style.ts';

/**
 * Geometry is expressed in a normalized 100x100 viewBox so the arc text scales
 * with `size` instead of being pinned to one diameter.
 */
const VIEWBOX = 100;
const CENTER = VIEWBOX / 2;
const TOP_ARC_RADIUS = CENTER - 19;
const DEFAULT_FONT_SIZE = 14;
const DEFAULT_LETTER_SPACING = 3;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 12px 0 var(--orb-glow-color) inset;
  }
  50% {
    box-shadow: 0 0 20px 0 var(--orb-glow-color) inset;
  }
`;

const circle = css`
  position: relative;
  border: 1px solid transparent;
  border-radius: 50%;
  /*
   * The padding-box layer is the fill and must stay opaque: it is what keeps
   * the border-box gradient confined to the 1px rim.
   */
  background:
    linear-gradient(var(--c-black), var(--c-black)) padding-box,
    var(--orb-rim) border-box;
  &.glowing {
    box-shadow: 0 0 12px 0 var(--orb-glow-color) inset;
  }
  &.loading {
    animation: ${glowPulse} 2.5s ease-in-out infinite;
  }
`;

const arcTextSvg = css`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  font-family: var(--brand-font);
  text-transform: uppercase;
`;

export type OrbArcText = {
  top?: string;
  bottom?: string;
  fromColor: string;
  toColor: string;
  fontSize?: number;
  letterSpacing?: number;
};

type Props = {
  size?: Unit;
  /** A color or a gradient. Drawn as the 1px rim. */
  rim?: string;
  /** Absent means no glow. */
  glowColor?: string;
  arcText?: OrbArcText;
  loading?: boolean;
  className?: string;
  children?: ReactNode;
};

export const OrbCircle = ({
  size = 5,
  rim = 'var(--c-nd-600)',
  glowColor,
  arcText,
  loading,
  className,
  children,
}: Props) => {
  const id = useId();
  const gradientId = `${id}-gradient`;
  const topArcId = `${id}-top-arc`;
  const bottomArcId = `${id}-bottom-arc`;

  const fontSize = arcText?.fontSize ?? DEFAULT_FONT_SIZE;
  const letterSpacing = arcText?.letterSpacing ?? DEFAULT_LETTER_SPACING;
  const r = TOP_ARC_RADIUS;
  // Bottom glyphs hang inside the circle, so push their baseline out by the
  // cap height to keep both arcs the same visual distance from the rim.
  const bottomArcRadius = r + fontSize * 0.7;
  // With no bottom label the top text runs on a full circle so long labels can
  // wrap down both sides; a bottom label needs that half of the ring kept free,
  // which caps the top text at a semicircle.
  const topArcPath = arcText?.bottom
    ? `M ${CENTER - r} ${CENTER} A ${r} ${r} 0 0 1 ${CENTER + r} ${CENTER}`
    : `M ${CENTER} ${CENTER + r} A ${r} ${r} 0 0 1 ${CENTER - r} ${CENTER} A ${r} ${r} 0 0 1 ${CENTER} ${CENTER - r} A ${r} ${r} 0 0 1 ${CENTER + r} ${CENTER} A ${r} ${r} 0 0 1 ${CENTER} ${CENTER + r}`;

  return (
    <Horizontal
      gap={0}
      center
      className={classNames(circle, className, {
        glowing: !!glowColor,
        loading: !!loading,
      })}
      style={
        {
          width: unitMapping[size],
          height: unitMapping[size],
          '--orb-rim': rim,
          '--orb-glow-color': glowColor,
        } as CSSProperties
      }
    >
      {!!arcText && (
        <svg
          className={arcTextSvg}
          viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
          aria-hidden
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="1" x2="1" y2="0">
              <stop offset="3%" stopColor={arcText.fromColor} />
              <stop offset="53%" stopColor={arcText.toColor} />
            </linearGradient>
            <path id={topArcId} fill="none" d={topArcPath} />
            <path
              id={bottomArcId}
              fill="none"
              d={`M ${CENTER - bottomArcRadius} ${CENTER} A ${bottomArcRadius} ${bottomArcRadius} 0 0 0 ${CENTER + bottomArcRadius} ${CENTER}`}
            />
          </defs>
          <text
            fill={`url(#${gradientId})`}
            style={{
              fontSize: `${fontSize}px`,
              letterSpacing: `${letterSpacing}px`,
            }}
          >
            {!!arcText.top && (
              <textPath
                href={`#${topArcId}`}
                startOffset="50%"
                textAnchor="middle"
              >
                {arcText.top}
              </textPath>
            )}
            {!!arcText.bottom && (
              <textPath
                href={`#${bottomArcId}`}
                startOffset="50%"
                textAnchor="middle"
              >
                {arcText.bottom}
              </textPath>
            )}
          </text>
        </svg>
      )}
      {children}
    </Horizontal>
  );
};
